import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  BarChart3,
  TrendingUp,
  Users,
  MapPin,
  Calendar,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Activity,
  PieChart,
  LineChart,
  Heart,
  MessageSquare,
  Image as ImageIcon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { getAllCategories } from '../data/categories';
import api from '../api/axios';
import { 
  PieChart as RechartsPie, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Legend, 
  Tooltip as RechartsTooltip,
  LineChart as RechartsLine,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart as RechartsBar,
  Bar
} from 'recharts';

const Analytics = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { favoritesCount } = useFavorites();
  const [stats, setStats] = useState({
    totalOffers: 0,
    activeUsers: 0,
    pendingOffers: 0,
    approvedThisMonth: 0,
    totalComments: 0,
    totalFavorites: 0,
    offersWithImages: 0
  });
  const [offers, setOffers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryStats, setCategoryStats] = useState([]);
  const [activeSection, setActiveSection] = useState(null); // null, 'offers', 'users', 'approval', 'pending'
  const [timelineData, setTimelineData] = useState([]);
  const allCategories = getAllCategories();

  useEffect(() => {
    if (!authLoading) {
      if (!user || !['owner', 'admin', 'moderator'].includes(user.role)) {
        navigate('/');
      } else {
        loadAnalytics();
      }
    }
  }, [user, authLoading, navigate]);

  const loadAnalytics = async () => {
    try {
      const [statsRes, offersRes, usersRes] = await Promise.all([
        api.get('/stats'),
        api.get('/offers/all'),
        api.get('/users')
      ]);
      
      const offersData = offersRes.data;
      const approvedOffers = offersData.filter(o => o.status === 'approved');
      
      // Calculer les nouvelles stats
      const totalComments = 0; // À calculer depuis l'API commentaires
      const offersWithImages = offersData.filter(o => o.image).length;
      
      setStats({
        ...statsRes.data,
        totalComments,
        totalFavorites: favoritesCount,
        offersWithImages
      });
      setOffers(offersData);
      setUsers(usersRes.data);
      
      // Calculate category statistics pour TOUTES les 19 catégories
      const categoryCount = {};
      
      // Initialiser toutes les catégories à 0
      allCategories.forEach(cat => {
        categoryCount[cat.id] = 0;
      });
      
      // Compter les offres par catégorie
      approvedOffers.forEach(offer => {
        if (categoryCount.hasOwnProperty(offer.category)) {
          categoryCount[offer.category]++;
        }
      });
      
      const categoryStatsArray = allCategories.map(cat => ({
        id: cat.id,
        name: cat.name,
        color: cat.color,
        count: categoryCount[cat.id] || 0,
        percentage: approvedOffers.length > 0 
          ? ((categoryCount[cat.id] / approvedOffers.length) * 100).toFixed(1)
          : 0
      })).sort((a, b) => b.count - a.count);
      
      setCategoryStats(categoryStatsArray);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate timeline data (last 7 days)
  useEffect(() => {
    const generateTimelineData = () => {
      const last7Days = [];
      const now = new Date();
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        // Count offers created on this day
        const offersCount = offers.filter(o => {
          if (!o.createdAt) return false;
          const offerDate = new Date(o.createdAt).toISOString().split('T')[0];
          return offerDate === dateStr;
        }).length;
        
        last7Days.push({
          date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
          offres: offersCount,
          fullDate: dateStr
        });
      }
      
      return last7Days;
    };
    
    if (offers.length > 0) {
      setTimelineData(generateTimelineData());
    }
  }, [offers]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-100 via-teal-100 to-cyan-200">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-2xl text-gray-600">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user || !['owner', 'admin', 'moderator'].includes(user.role)) {
    return null;
  }

  const approvedOffers = offers.filter(o => o.status === 'approved');
  const pendingOffers = offers.filter(o => o.status === 'pending');
  const rejectedOffers = offers.filter(o => o.status === 'rejected');
  
  const approvalRate = offers.length > 0 
    ? ((approvedOffers.length / offers.length) * 100).toFixed(1) 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-100 via-teal-100 to-cyan-200">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au Dashboard
          </Button>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-800">Analyses Détaillées</h1>
                <p className="text-gray-600">Statistiques et rapports de la plateforme</p>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card 
              className={`border-none shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer ${activeSection === 'offers' ? 'ring-4 ring-purple-500' : 'hover:scale-105'}`}
              onClick={() => setActiveSection(activeSection === 'offers' ? null : 'offers')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Offres</CardTitle>
                <MapPin className="w-4 h-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{stats.totalOffers}</div>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowDown className="w-3 h-3 text-purple-500" />
                  <p className="text-xs text-purple-500">
                    {activeSection === 'offers' ? 'Masquer ↑' : 'Voir détails ↓'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`border-none shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer ${activeSection === 'users' ? 'ring-4 ring-blue-500' : 'hover:scale-105'}`}
              onClick={() => setActiveSection(activeSection === 'users' ? null : 'users')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Utilisateurs</CardTitle>
                <Users className="w-4 h-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{stats.activeUsers}</div>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowDown className="w-3 h-3 text-blue-500" />
                  <p className="text-xs text-blue-500">
                    {activeSection === 'users' ? 'Masquer ↑' : 'Voir liste ↓'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`border-none shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer ${activeSection === 'approval' ? 'ring-4 ring-emerald-500' : 'hover:scale-105'}`}
              onClick={() => setActiveSection(activeSection === 'approval' ? null : 'approval')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Taux d'Approbation</CardTitle>
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{approvalRate}%</div>
                <p className="text-xs text-gray-500 mt-2">{approvedOffers.length} / {offers.length} offres</p>
              </CardContent>
            </Card>

            <Card 
              className={`border-none shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer ${activeSection === 'pending' ? 'ring-4 ring-orange-500' : 'hover:scale-105'}`}
              onClick={() => setActiveSection(activeSection === 'pending' ? null : 'pending')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">En Attente</CardTitle>
                <Activity className="w-4 h-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{stats.pendingOffers}</div>
                <p className="text-xs text-gray-500 mt-2">
                  {activeSection === 'pending' ? 'Masquer ↑' : 'Voir détails ↓'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Dynamic Content Section */}
          {activeSection === 'offers' && (
            <Card className="border-none shadow-lg mb-8 animate-in fade-in slide-in-from-top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-500" />
                  Répartition des Offres
                </CardTitle>
                <CardDescription>Distribution par statut et catégorie</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-green-50 rounded-lg p-6 text-center">
                    <div className="text-4xl font-bold text-green-600">{approvedOffers.length}</div>
                    <p className="text-sm text-gray-600 mt-2">Approuvées</p>
                    <div className="w-full bg-green-200 h-2 rounded-full mt-3">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all" 
                        style={{width: `${(approvedOffers.length / stats.totalOffers * 100)}%`}}
                      />
                    </div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-6 text-center">
                    <div className="text-4xl font-bold text-orange-600">{pendingOffers.length}</div>
                    <p className="text-sm text-gray-600 mt-2">En Attente</p>
                    <div className="w-full bg-orange-200 h-2 rounded-full mt-3">
                      <div 
                        className="bg-orange-600 h-2 rounded-full transition-all" 
                        style={{width: `${(pendingOffers.length / stats.totalOffers * 100)}%`}}
                      />
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-6 text-center">
                    <div className="text-4xl font-bold text-red-600">{rejectedOffers.length}</div>
                    <p className="text-sm text-gray-600 mt-2">Refusées</p>
                    <div className="w-full bg-red-200 h-2 rounded-full mt-3">
                      <div 
                        className="bg-red-600 h-2 rounded-full transition-all" 
                        style={{width: `${(rejectedOffers.length / stats.totalOffers * 100)}%`}}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-purple-600" />
                      Offres avec Images
                    </h4>
                    <div className="text-3xl font-bold text-purple-600">{stats.offersWithImages}</div>
                    <p className="text-xs text-gray-500 mt-1">
                      {((stats.offersWithImages / stats.totalOffers) * 100).toFixed(1)}% du total
                    </p>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-pink-600" />
                      Commentaires
                    </h4>
                    <div className="text-3xl font-bold text-pink-600">{stats.totalComments}</div>
                    <p className="text-xs text-gray-500 mt-1">
                      {stats.totalOffers > 0 ? (stats.totalComments / stats.totalOffers).toFixed(1) : 0} par offre
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'users' && (
            <Card className="border-none shadow-lg mb-8 animate-in fade-in slide-in-from-top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Liste des Utilisateurs
                </CardTitle>
                <CardDescription>Tous les utilisateurs de la plateforme</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((u, index) => (
                    <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold">{u.name}</p>
                          <p className="text-sm text-gray-500">{u.email}</p>
                        </div>
                      </div>
                      <Badge className="capitalize">
                        {u.role === 'owner' ? '👑 Propriétaire' :
                         u.role === 'admin' ? 'Admin' :
                         u.role === 'moderator' ? 'Modérateur' : 'Utilisateur'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'approval' && (
            <Card className="border-none shadow-lg mb-8 animate-in fade-in slide-in-from-top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  Statistiques d'Approbation
                </CardTitle>
                <CardDescription>Vue détaillée des taux d'approbation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">Taux Global</h3>
                    <div className="flex items-center gap-4">
                      <div className="text-6xl font-bold text-emerald-600">{approvalRate}%</div>
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 h-6 rounded-full overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-emerald-500 to-teal-500 h-6 rounded-full flex items-center justify-end px-2 transition-all"
                            style={{width: `${approvalRate}%`}}
                          >
                            <span className="text-white text-xs font-bold">{approvalRate}%</span>
                          </div>
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-gray-600">
                          <span>0%</span>
                          <span>50%</span>
                          <span>100%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Offres Approuvées</h4>
                      <div className="text-3xl font-bold text-blue-600">{approvedOffers.length}</div>
                      <p className="text-xs text-gray-600 mt-1">Visibles publiquement</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900 mb-2">Offres Refusées</h4>
                      <div className="text-3xl font-bold text-red-600">{rejectedOffers.length}</div>
                      <p className="text-xs text-gray-600 mt-1">Non conformes</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'pending' && (
            <Card className="border-none shadow-lg mb-8 animate-in fade-in slide-in-from-top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-orange-500" />
                  Offres en Attente
                </CardTitle>
                <CardDescription>Nécessitent votre validation</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingOffers.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Activity className="w-16 h-16 mx-auto mb-3 opacity-20" />
                    <p className="text-lg">Aucune offre en attente</p>
                    <p className="text-sm mt-2">Toutes les offres ont été traitées</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingOffers.map(offer => (
                      <div key={offer.id} className="border border-orange-200 rounded-lg p-4 bg-orange-50 hover:bg-orange-100 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-lg">{offer.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{offer.description}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                              <MapPin className="w-3 h-3" />
                              {offer.address}
                            </div>
                          </div>
                          <Button 
                            onClick={() => navigate(`/offre/${offer.id}`)}
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600"
                          >
                            Examiner →
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Tabs defaultValue="categories" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="categories">
                <PieChart className="w-4 h-4 mr-2" />
                Par Catégories
              </TabsTrigger>
              <TabsTrigger value="timeline">
                <LineChart className="w-4 h-4 mr-2" />
                Évolution
              </TabsTrigger>
              <TabsTrigger value="users">
                <Users className="w-4 h-4 mr-2" />
                Utilisateurs
              </TabsTrigger>
            </TabsList>

            {/* Category Analytics */}
            <TabsContent value="categories" className="space-y-4">
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle>Distribution par Catégories (19 catégories)</CardTitle>
                  <CardDescription>Répartition des offres approuvées par catégorie</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {categoryStats.map((cat, index) => (
                      <div key={cat.id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: cat.color }}
                            >
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800 text-sm">
                                {cat.name}
                              </p>
                              <p className="text-xs text-gray-500">{cat.count} offres</p>
                            </div>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {cat.percentage}%
                          </Badge>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-500"
                            style={{ 
                              width: `${cat.percentage}%`,
                              backgroundColor: cat.color
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Graphique Camembert des Catégories */}
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle>Répartition Visuelle des Catégories</CardTitle>
                  <CardDescription>Vue d'ensemble en camembert</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={categoryStats.slice(0, 10)} // Top 10 categories
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${name} (${percentage}%)`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {categoryStats.slice(0, 10).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                                  <p className="font-semibold">{payload[0].name}</p>
                                  <p className="text-sm text-gray-600">
                                    {payload[0].value} offres ({payload[0].payload.percentage}%)
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          formatter={(value, entry) => `${value} (${entry.payload.count})`}
                        />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Nouvelles Stats - Commentaires & Favoris */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-none shadow-lg bg-gradient-to-br from-pink-500 to-red-500 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Commentaires
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats.totalComments || 0}</div>
                    <p className="text-sm text-pink-100">Total avis postés</p>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg bg-gradient-to-br from-red-400 to-pink-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Heart className="w-5 h-5" />
                      Favoris
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats.totalFavorites || 0}</div>
                    <p className="text-sm text-red-100">Offres en favoris</p>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ImageIcon className="w-5 h-5" />
                      Images
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats.offersWithImages || 0}</div>
                    <p className="text-sm text-blue-100">Offres avec images</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Timeline Analytics */}
            <TabsContent value="timeline" className="space-y-4">
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle>Évolution dans le Temps</CardTitle>
                  <CardDescription>Activité de la plateforme sur les 30 derniers jours</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80 mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsLine data={timelineData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '8px',
                            padding: '10px'
                          }}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="offres" 
                          name="Nouvelles offres"
                          stroke="#8b5cf6" 
                          strokeWidth={3}
                          dot={{ fill: '#8b5cf6', r: 5 }}
                          activeDot={{ r: 8 }}
                        />
                      </RechartsLine>
                    </ResponsiveContainer>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-none">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-emerald-600">{stats.approvedThisMonth}</div>
                          <p className="text-sm text-gray-600 mt-1">Approuvées ce mois</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-none">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600">{pendingOffers.length}</div>
                          <p className="text-sm text-gray-600 mt-1">En attente</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-none">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-600">{rejectedOffers.length}</div>
                          <p className="text-sm text-gray-600 mt-1">Refusées</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* User Analytics */}
            <TabsContent value="users" className="space-y-4">
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle>Statistiques Utilisateurs</CardTitle>
                  <CardDescription>Répartition et activité des membres</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-800">Par Rôle</h3>
                      {['owner', 'admin', 'moderator', 'user'].map(role => {
                        const count = users.filter(u => u.role === role).length;
                        const percentage = users.length > 0 ? ((count / users.length) * 100).toFixed(1) : 0;
                        return (
                          <div key={role} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium capitalize">{role}</p>
                              <Badge variant="outline">{count} ({percentage}%)</Badge>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-semibold text-slate-800">Top Contributeurs</h3>
                      {users.slice(0, 5).map((u, index) => {
                        const userOffers = offers.filter(o => o.createdBy === u.id && o.status === 'approved').length;
                        return (
                          <div key={u.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-white to-purple-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{u.name}</p>
                                <p className="text-xs text-gray-500">{userOffers} offres</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Analytics;
